import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Play, X } from "@phosphor-icons/react";
import { formatDuration } from "@/lib/api";
import { getMaturity, getGenreLabel } from "@/lib/meta";
import { onImgError } from "@/lib/fallback";

const COUNTDOWN = 5;

export const UpNextOverlay = ({ next, tvMode, onCancel }) => {
  const navigate = useNavigate();
  const [remaining, setRemaining] = useState(COUNTDOWN);
  const playRef = useRef(null);

  const play = () => navigate(`/watch/${next.video_id}?autoplay=1`);

  useEffect(() => {
    if (playRef.current) playRef.current.focus();
    const start = Date.now();
    const iv = setInterval(() => {
      const rem = Math.max(0, COUNTDOWN - (Date.now() - start) / 1000);
      setRemaining(rem);
      if (rem <= 0) {
        clearInterval(iv);
        play();
      }
    }, 100);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [next.video_id]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" || e.key === "ArrowDown") {
        e.preventDefault();
        e.stopImmediatePropagation();
        onCancel();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onCancel]);

  const pct = (remaining / COUNTDOWN) * 100;
  const poster = next.thumbnails?.[1] || next.default_thumb;
  const maturity = getMaturity(next);
  const genre = getGenreLabel(next);

  const wrap = tvMode
    ? "fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-md"
    : "absolute bottom-4 right-4 z-[60] w-[340px] sm:w-[380px]";

  return (
    <div className={wrap} data-testid="up-next-overlay">
      <div className={`border border-white/10 bg-black/80 backdrop-blur-md shadow-2xl ${tvMode ? "w-[640px] p-8" : "p-4"}`}>
        <div className="flex items-center justify-between mb-3">
          <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-amber">Up Next</span>
          <button
            data-testid="up-next-cancel"
            onClick={onCancel}
            className="flex items-center gap-1.5 border border-white/15 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-white/60 hover:border-crimson hover:text-crimson transition-colors duration-300"
          >
            <X size={12} weight="bold" /> Cancel
          </button>
        </div>

        <div className="flex gap-4">
          <div className={`shrink-0 overflow-hidden border border-white/10 bg-black ${tvMode ? "w-32 h-48" : "w-24 h-36"}`}>
            <img src={poster} alt={next.title} onError={onImgError} className="h-full w-full object-cover" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className={`font-serif leading-tight text-[#EDEDED] ${tvMode ? "text-2xl" : "text-lg"} line-clamp-3`}>
              {next.title}
            </h3>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span
                data-testid="up-next-free-badge"
                className="bg-emerald-500 text-black px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-[0.1em]"
              >
                Free
              </span>
              <span className="border border-white/25 text-white/75 px-2 py-0.5 font-mono text-[10px] tracking-[0.1em]">
                {maturity}
              </span>
              {next.release_year && (
                <span className="font-mono text-[11px] text-white/45">{next.release_year}</span>
              )}
              <span className="font-mono text-[11px] text-amber">{genre}</span>
              <span className="font-mono text-[11px] text-white/40">{formatDuration(next.duration_sec)}</span>
            </div>

            <button
              ref={playRef}
              data-testid="up-next-play"
              onClick={play}
              className="mt-4 inline-flex items-center gap-2 bg-amber text-black px-5 py-2.5 font-mono text-xs uppercase tracking-[0.15em] hover:bg-crimson hover:text-white transition-colors duration-300"
            >
              <Play size={16} weight="fill" /> Play Now
            </button>
          </div>
        </div>

        {/* Countdown progress bar */}
        <div className="mt-4 h-1 w-full bg-white/10 overflow-hidden">
          <div className="h-full bg-amber transition-[width] duration-100 ease-linear" style={{ width: `${pct}%` }} />
        </div>
        <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.15em] text-white/40">
          Playing in {Math.ceil(remaining)}s {tvMode && "· Press OK to play, ↓ to cancel"}
        </p>
      </div>
    </div>
  );
};
